import { useCallback, useEffect, useRef, useState } from "react";
import {
  loadProjectOverviewBundle,
  type ProjectOverviewBundle,
} from "@/lib/projectOverview";

function bundlesEqual(a: ProjectOverviewBundle, b: ProjectOverviewBundle): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function useProjectOverviewData(
  orderId: number | null,
  enabled: boolean,
  includeBueroSections = true,
) {
  const [data, setData] = useState<ProjectOverviewBundle | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generationRef = useRef(0);
  const dataRef = useRef<ProjectOverviewBundle | null>(null);
  dataRef.current = data;

  const refetch = useCallback(async () => {
    if (!orderId || !enabled) return;
    const gen = ++generationRef.current;
    const shown = dataRef.current;
    const silent = shown != null && shown.project.id === orderId;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const bundle = await loadProjectOverviewBundle(orderId, { includeBueroSections });
      if (gen !== generationRef.current) return;
      setData((prev) => {
        if (prev && prev.project.id === bundle.project.id && bundlesEqual(prev, bundle)) {
          return prev;
        }
        return bundle;
      });
      if (silent) setError(null);
    } catch (err) {
      if (gen !== generationRef.current) return;
      if (!silent) {
        setError(err instanceof Error ? err.message : "Laden fehlgeschlagen");
        setData(null);
      }
    } finally {
      if (gen === generationRef.current) setLoading(false);
    }
  }, [orderId, enabled, includeBueroSections]);

  useEffect(() => {
    if (!enabled || !orderId) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    void refetch();
  }, [enabled, orderId, refetch]);

  const patch = useCallback((partial: Partial<ProjectOverviewBundle>) => {
    setData((prev) => (prev ? { ...prev, ...partial } : prev));
  }, []);

  return { data, loading, error, refetch, patch };
}
